/**
 * Наплата — payment register ("каса" for member payments) inside the Members page.
 * Left: entry form (member → plan → editable sum → Pay). Right: member context panel
 * (status, plan, expiry, credits) when a real member is selected. Pay opens a single
 * fiscalize Да/Не popup. No payment-method field; no payments list (lives in Analytics).
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button.js';
import { Card, CardBody, CardHeader } from '../../components/ui/Card.js';
import { Field, Input, Select } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { Money, useFormatters } from '../../components/ui/Money.js';
import { StatusPill, statusTone } from '../../components/ui/StatusPill.js';
import { usePosContext } from '../../lib/pos.js';
import { supabase } from '../../lib/supabase.js';
import { resolveDefaultPlan } from '../payments/defaultFee.js';
import { useRecordPayment, type RecordPaymentArgs } from '../payments/useRecordPayment.js';

type PayKind = RecordPaymentArgs['kind'];

/** Maps a plan_type (DB enum) to the payment kind recorded for it. */
const PLAN_TYPE_TO_KIND: Record<string, PayKind> = {
  monthly: 'membership',
  quarterly: 'membership',
  annual: 'membership',
  unlimited: 'membership',
  off_peak: 'membership',
  session_pack: 'membership',
  punch_card: 'membership',
  day_pass: 'day_pass',
  class_pack: 'class_pack',
  pt_pack: 'pt_session',
};

interface MemberOpt {
  id: string;
  first_name: string;
  last_name: string;
}

interface PlanOpt {
  id: string;
  name: string;
  plan_type: string;
  price: number;
  sort_order: number | null;
}

interface MemberCtx {
  first_name: string;
  last_name: string;
  membership_status: string | null;
  plan_type: string | null;
  end_date: string | null;
  credits_remaining: number | null;
}

export function PaymentsTab({
  tenantId,
  initialMemberId,
}: {
  tenantId: string;
  initialMemberId?: string | null | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const pos = usePosContext();

  const [memberId, setMemberId] = useState('');
  const [planId, setPlanId] = useState('');
  const [amountMajor, setAmountMajor] = useState('');
  const [fiscalOpen, setFiscalOpen] = useState(false);

  // Member list (walk-in is the empty option)
  const members = useQuery({
    queryKey: ['member_options_payments', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<MemberOpt[]> => {
      const { data, error } = await supabase
        .from('v_member_list')
        .select('id, first_name, last_name')
        .eq('tenant_id', tenantId)
        .order('first_name')
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as MemberOpt[];
    },
  });

  // Active plans, ordered for selection
  const plans = useQuery({
    queryKey: ['plans', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<PlanOpt[]> => {
      const { data, error } = await supabase
        .from('plans')
        .select('id, name, plan_type, price, sort_order')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as unknown as PlanOpt[];
    },
  });

  const planList = plans.data ?? [];

  /** Selects a plan id + sets the amount to that plan's price (still editable after). */
  function selectPlan(id: string): void {
    setPlanId(id);
    const p = planList.find((pl) => pl.id === id);
    if (p) setAmountMajor(String(p.price / 100));
  }

  /**
   * On member change: walk-in → default to the first plan; a real member → resolve
   * their current plan and preselect it (fallback to first plan when none resolved).
   */
  async function handleMemberChange(id: string): Promise<void> {
    setMemberId(id);
    if (!id) {
      const first = planList[0];
      if (first) selectPlan(first.id);
      return;
    }
    const resolved = await resolveDefaultPlan(supabase, tenantId, id);
    if (resolved && planList.some((p) => p.id === resolved.planId)) {
      setPlanId(resolved.planId);
      setAmountMajor(String(resolved.amountMinor / 100));
    } else {
      const first = planList[0];
      if (first) selectPlan(first.id);
    }
  }

  // Preselect a member handed in from the directory (drawer "Запиши плаќање"). Wait for
  // plans to load so the resolved plan can be matched, and run once per id change.
  const appliedInitial = useRef<string | null>(null);
  useEffect(() => {
    if (!initialMemberId) return;
    if (!plans.data) return;
    if (appliedInitial.current === initialMemberId) return;
    appliedInitial.current = initialMemberId;
    void handleMemberChange(initialMemberId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMemberId, plans.data]);

  // Member context panel — fetched from v_member_list for the selected (real) member.
  const memberCtx = useQuery({
    queryKey: ['member_charge_ctx', memberId],
    enabled: !!memberId,
    queryFn: async (): Promise<MemberCtx | null> => {
      const { data, error } = await supabase
        .from('v_member_list')
        .select('first_name, last_name, membership_status, plan_type, end_date, credits_remaining')
        .eq('id', memberId)
        .limit(1);
      if (error) throw error;
      return ((data ?? [])[0] ?? null) as unknown as MemberCtx | null;
    },
  });

  const selectedPlan = planList.find((p) => p.id === planId) ?? null;
  const kind: PayKind = selectedPlan
    ? (PLAN_TYPE_TO_KIND[selectedPlan.plan_type] ?? 'other')
    : 'other';

  const amountMinor = Math.round(Number(amountMajor) * 100);
  const canPay = !!selectedPlan && amountMinor > 0;

  const record = useRecordPayment(() => {
    setFiscalOpen(false);
    // Keep member + plan selected; clear the sum so the next charge re-fills cleanly.
    if (selectedPlan) setAmountMajor(String(selectedPlan.price / 100));
  });

  function pay(fiscalize: boolean): void {
    if (!selectedPlan || amountMinor <= 0) return;
    record.mutate({
      memberId: memberId || null,
      membershipPlanId: selectedPlan.id,
      kind,
      amountMinor,
      fiscalize,
      lineItemName: selectedPlan.name,
    });
  }

  // Device is only required when fiscalizing; missing device must not block "Не".
  const missingCore = pos.missing.filter((m) => m !== 'device');
  const blocked = missingCore.length > 0;

  const ctx = memberCtx.data ?? null;

  return (
    <>
      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:items-start">
        {/* ── Entry form ──────────────────────────────────────────────────── */}
        <Card>
          <CardHeader title={t('members.tabs.payments')} subtitle={t('billing.subtitle')} />
          <CardBody className="grid gap-4">
            {/* Member */}
            <Field label={t('members.title')}>
              {(id) => (
                <Select
                  id={id}
                  value={memberId}
                  onChange={(e) => void handleMemberChange(e.target.value)}
                >
                  <option value="">{t('billing.walkIn')}</option>
                  {(members.data ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.first_name} {m.last_name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {/* Plan (preselected to the member's current plan) */}
            <Field
              label={t('memberships.plan')}
              hint={
                selectedPlan ? `${selectedPlan.name} · ${fmt.money(selectedPlan.price)}` : undefined
              }
            >
              {(id) => (
                <Select
                  id={id}
                  value={planId}
                  onChange={(e) => selectPlan(e.target.value)}
                  disabled={plans.isLoading}
                >
                  <option value="" disabled>
                    {t('memberships.plan')}
                  </option>
                  {planList.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {fmt.money(p.price)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {/* Sum (auto-filled from plan price, editable) */}
            <Field label={`${t('billing.amount')} (${t('common.currency')})`} required>
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min="0"
                  step="1"
                  value={amountMajor}
                  onChange={(e) => setAmountMajor(e.target.value)}
                  placeholder="0"
                />
              )}
            </Field>

            {/* To-pay summary */}
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface-2 px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-xs font-medium uppercase tracking-wide text-faint">
                  {t('members.charge.toPay')}
                </div>
                <div className="truncate text-sm text-muted">
                  {selectedPlan ? selectedPlan.name : '—'}
                </div>
              </div>
              <Money minor={amountMinor} className="text-base text-success" />
            </div>

            {blocked && !pos.loading ? (
              <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-2 text-xs text-warning">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{missingCore.map((m) => t(`billing.missing.${m}`)).join(', ')}</span>
              </div>
            ) : null}

            <Button
              variant="primary"
              disabled={!canPay || blocked}
              loading={record.isPending}
              onClick={() => setFiscalOpen(true)}
            >
              {t('billing.recordPayment')}
            </Button>
          </CardBody>
        </Card>

        {/* ── Member context panel ────────────────────────────────────────── */}
        <Card>
          <CardHeader title={t('members.charge.memberContext')} />
          <CardBody>
            {!memberId ? (
              <p className="text-sm text-muted">{t('members.charge.walkInHint')}</p>
            ) : memberCtx.isLoading ? (
              <p className="text-sm text-faint">…</p>
            ) : ctx ? (
              <div className="grid gap-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-display text-lg font-semibold leading-tight text-fg">
                    {ctx.first_name} {ctx.last_name}
                  </span>
                  {ctx.membership_status ? (
                    <StatusPill
                      status={ctx.membership_status}
                      tone={statusTone(ctx.membership_status)}
                      label={t(`memberships.status.${ctx.membership_status}`, {
                        defaultValue: ctx.membership_status,
                      })}
                    />
                  ) : null}
                </div>
                <div className="divide-y divide-border">
                  <CtxRow label={t('memberships.plan')}>
                    {ctx.plan_type
                      ? t(`plans.type.${ctx.plan_type}`, { defaultValue: ctx.plan_type })
                      : '—'}
                  </CtxRow>
                  <CtxRow label={t('memberships.endDate')}>
                    {ctx.end_date ? fmt.date(ctx.end_date) : '—'}
                  </CtxRow>
                  {ctx.credits_remaining != null ? (
                    <CtxRow label={t('memberships.creditsRemaining')}>
                      {ctx.credits_remaining}
                    </CtxRow>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="text-sm text-faint">—</p>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Fiscalize prompt — both buttons record the payment; close cancels. */}
      <Modal
        open={fiscalOpen}
        onOpenChange={(o) => {
          if (!record.isPending) setFiscalOpen(o);
        }}
        title={t('billing.fiscalize.prompt')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" disabled={record.isPending} onClick={() => pay(false)}>
              {t('billing.fiscalize.no')}
            </Button>
            <Button variant="primary" loading={record.isPending} onClick={() => pay(true)}>
              {t('billing.fiscalize.yes')}
            </Button>
          </>
        }
      />
    </>
  );
}

function CtxRow({ label, children }: { label: ReactNode; children: ReactNode }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm font-medium text-fg">{children}</span>
    </div>
  );
}
